import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface ParkingSpot {
  id: string
  spot_number: string
  spot_type: string
  location: string
  is_occupied: boolean
  is_reserved: boolean
  has_ev_charger: boolean
}

interface Permit {
  id: string
  permit_number: string
  permit_type: string
  employee_name: string
  vehicle_plate: string
  vehicle_make: string
  expiry_date: string
  status: string
}

export default function ParkingPage() {
  const [spots, setSpots] = useState<ParkingSpot[]>([])
  const [permits, setPermits] = useState<Permit[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [spotsRes, permRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/parking/spots'),
      fetch(API_BASE + '/parking/permits'),
      fetch(API_BASE + '/parking/statistics')
    ])
    if (spotsRes.ok) setSpots((await spotsRes.json()).parking_spots || [])
    if (permRes.ok) setPermits((await permRes.json()).permits || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-blue-100 text-blue-800'
      case 'expired': case 'suspended': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Parking Management</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Reserve Spot</button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ New Permit</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Spots</div>
              <div className="text-2xl font-bold">{statistics.total_spots}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Available</div>
              <div className="text-2xl font-bold text-green-600">{statistics.available_spots}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Occupancy</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.occupancy_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">EV Spots</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.ev_charging_spots}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Permits</div>
              <div className="text-2xl font-bold">{statistics.active_permits}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Parking Spots</h2></div>
            <div className="p-6">
              <div className="grid grid-cols-10 gap-2">
                {spots.slice(0, 100).map(spot => (
                  <div
                    key={spot.id}
                    className={`p-2 rounded text-center text-xs ${
                      spot.is_occupied
                        ? 'bg-red-100 text-red-800'
                        : spot.is_reserved
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}
                    title={`${spot.spot_number} - ${spot.spot_type} - ${spot.location}`}
                  >
                    {spot.spot_number.split('-')[1]}
                    {spot.has_ev_charger && <span className="block text-purple-600">EV</span>}
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-4 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 rounded"></span> Available</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-100 rounded"></span> Reserved</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 rounded"></span> Occupied</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Active Permits</h3>
            <div className="space-y-3">
              {permits.filter(p => p.status === 'active').slice(0, 10).map(permit => (
                <div key={permit.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{permit.employee_name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(permit.status)}`}>{permit.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 font-mono">{permit.permit_number}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-600">{permit.vehicle_make} - {permit.vehicle_plate}</span>
                    <span className="text-xs text-gray-500">Exp: {permit.expiry_date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
